import { useMemo, useRef, useState } from "react";
import AppDrawer from "./AppDrawer";
import { Button, Alert } from "reactstrap";
import { useAtomValue } from "jotai";
import { modulesAtom } from "../../atoms/GlobalAtoms";
import { PickerHandle, SelectPicker } from "rsuite";
import useRecords from "../../api/api-hooks/records/useRecords";
import RequiredTitle from "../../elements/RequiredTitle";
import useModule from "../../hooks/useModule";
import useCreateRecords from "../../api/api-hooks/records/useCreateRecords";
import { newToastEvent } from "../../utils/ToastUtil";

interface Props {
  open: boolean;
  moduleAPIName: string;
  recordIDs: string[];
  handleClose: () => void;
}

// TODO: Move Records Select Picker to use SingleReference component (it has pagination built in)
const AddToListDrawer = ({
  open,
  moduleAPIName,
  recordIDs,
  handleClose,
}: Props) => {
  const [error, setError] = useState("");
  const [selectedListModule, setSelectedListModule] = useState("");
  const [selectedListID, setSelectedListID] = useState("");
  const pickerRef = useRef<PickerHandle>(null);

  const modules = useAtomValue(modulesAtom);
  const { module: listModule } = useModule(selectedListModule);
  const { records } = useRecords({
    moduleAPIName: selectedListModule,
    disabled: !open,
  });
  const { createRecords } = useCreateRecords();

  const availableListModules = useMemo(() => {
    if (!modules || !modules.length) return [];

    return modules.filter((mod) => !!mod?.ModuleList?.IsModuleList);
  }, [modules]);

  return (
    <AppDrawer
      open={open}
      placement="right"
      size="md"
      backdrop="static"
      onClose={handleCloseDrawer}
      onEntered={() => pickerRef.current?.open?.()}
      headerTitle={"Add To List"}
      headerDescription={"Add the selected records to one of your Lists"}
      headerOptions={
        <Button
          size="sm"
          style={{ width: 80 }}
          color="primary"
          onClick={handleAddRecordsToList}
        >
          Add
        </Button>
      }
      body={
        <div className="d-flex flex-column">
          {availableListModules.length > 0 ? (
            <>
              <RequiredTitle title="List Data Source" size="md" />
              <SelectPicker
                ref={pickerRef}
                placeholder="List Data Source"
                data={availableListModules}
                labelKey="Name"
                valueKey="APIName"
                searchable={availableListModules?.length > 5}
                cleanable={false}
                value={selectedListModule}
                onSelect={(moduleList) => handleSelectListModule(moduleList)}
              />
            </>
          ) : (
            <Alert color="warning">
              You don't have any data sources configured as a List.
            </Alert>
          )}
          {selectedListModule && records && (
            <>
              <RequiredTitle title="List" size="md" className="mt-3" />
              <SelectPicker
                placeholder="List"
                data={records}
                labelKey={"Name"}
                valueKey="_id"
                searchable={records?.length > 5}
                value={selectedListID}
                onSelect={(record) => handleSelectList(record)}
                defaultOpen={true}
                cleanable={false}
              />
            </>
          )}
          {error && (
            <Alert
              color="warning"
              className="font-weight-500 font-weight-500 m-2"
            >
              {error}
            </Alert>
          )}
        </div>
      }
    />
  );

  function handleSelectListModule(moduleList: any) {
    setSelectedListModule(moduleList);
  }

  function handleSelectList(record: any) {
    setSelectedListID(record);
  }

  function handleCloseDrawer() {
    handleClose();
    setSelectedListID("");
    setSelectedListModule("");
  }

  // Add the selected records to the selected list.
  // List module, list record must be selected first.
  function handleAddRecordsToList() {
    const listItems: any = [];

    if (
      typeof listModule?.ModuleList?.ListReferenceField !== "string" ||
      typeof listModule?.ModuleList?.ItemsReferenceField !== "string" ||
      typeof listModule?.ModuleList?.ListItemsModule !== "string"
    ) {
      return listItems;
    }

    const selectedListRecord = records.find(
      (r: any) => r._id === selectedListID
    );

    for (let i = 0; i < recordIDs.length; i++) {
      const recordId = recordIDs[i];

      const newListItem = {
        [listModule.ModuleList.ListReferenceField]: {
          id: selectedListRecord.id,
          _id: selectedListRecord._id,
          _ModuleAPIName: selectedListRecord._ModuleAPIName,
        },
        [listModule.ModuleList.ItemsReferenceField]: {
          id: recordId,
          _id: recordId,
          _ModuleAPIName: moduleAPIName,
        },
      };

      listItems.push(newListItem);
    }

    createRecords({
      moduleAPIName: listModule.ModuleList.ListItemsModule,
      payload: listItems,
      onCreate: () => {
        newToastEvent(
          "success",
          `Successfully added records to ${selectedListRecord?._RecordName} list`
        );
        handleCloseDrawer();
      },
      onError: setError,
    });
  }
};

export default AddToListDrawer;
